import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserStore } from '../../../store/userStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
  fallback?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  fallback,
}) => {
  const { currentUser, isLoading } = useUserStore();
  const location = useLocation();
  const isAuthenticated = !!currentUser;

  // 로딩 중일 때 로딩 스피너 표시
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">인증 정보를 확인하는 중...</p>
        </div>
      </div>
    );
  }

  // 인증되지 않은 경우 독서실 홈페이지로 리다이렉트
  if (!isAuthenticated || !currentUser) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // 역할 기반 접근 제어 (Role-based Access Control)
  if (requiredRole && currentUser.role !== requiredRole) {
    console.log('🚫 권한 부족:', {
      required: requiredRole,
      actual: currentUser.role,
      path: location.pathname,
    });

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-red-50 rounded-lg p-8">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              접근 권한이 없습니다
            </h2>
            <p className="text-gray-600 mb-4">
              이 페이지에 접근하려면 <strong>{requiredRole}</strong> 권한이
              필요합니다.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              현재 권한: {currentUser.role}
            </p>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              이전 페이지로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 권한 기반 접근 제어 (Permission-based Access Control)
  if (requiredPermission) {
    // 실제 구현에서는 사용자의 permissions 배열을 확인
    const hasPermission =
      currentUser.permissions &&
      currentUser.permissions.includes(requiredPermission);

    if (!hasPermission) {
      console.log('🚫 권한 부족:', {
        required: requiredPermission,
        userPermissions: currentUser.permissions,
        path: location.pathname,
      });

      if (fallback) {
        return <>{fallback}</>;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto">
            <div className="bg-orange-50 rounded-lg p-8">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-orange-100 rounded-full">
                <svg
                  className="w-6 h-6 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                권한이 부족합니다
              </h2>
              <p className="text-gray-600 mb-4">
                이 기능을 사용하려면 <strong>{requiredPermission}</strong>{' '}
                권한이 필요합니다.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                시스템 관리자에게 권한 부여를 요청하세요.
              </p>
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                이전 페이지로 돌아가기
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // 모든 조건을 통과한 경우 자식 컴포넌트 렌더링
  console.log('✅ 접근 허용:', {
    user: currentUser.name,
    role: currentUser.role,
    path: location.pathname,
  });

  return <>{children}</>;
};

// 역할별 특화된 Protected Route 컴포넌트들
export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // admin_token이 있으면 관리자로 간주 (zustand hydration 타이밍 이슈 해결)
  const adminToken = localStorage.getItem('admin_token');
  const adminUser = localStorage.getItem('admin_user');

  if (adminToken && adminUser) {
    console.log('✅ [AdminRoute] admin_token 확인됨, 접근 허용');
    return <>{children}</>;
  }

  return <ProtectedRoute requiredRole="admin">{children}</ProtectedRoute>;
};

export const OwnerRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <ProtectedRoute requiredRole="owner">{children}</ProtectedRoute>;

export const StaffRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <ProtectedRoute requiredRole="staff">{children}</ProtectedRoute>;

// 사용자(고객) 전용 라우트
export const UserRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <ProtectedRoute requiredRole="user">{children}</ProtectedRoute>;

// 관리자 또는 오너만 접근 가능
export const ManagerRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser, isLoading } = useUserStore();
  const location = useLocation();

  // 오너 토큰 확인 (userStore 기반)
  const authToken = localStorage.getItem('authToken');

  // 로딩 중일 때 로딩 스피너 표시
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">인증 정보를 확인하는 중...</p>
        </div>
      </div>
    );
  }

  // 오너로 로그인한 경우
  if (
    currentUser &&
    currentUser.role === 'owner' &&
    authToken === 'owner-demo-token'
  ) {
    return <>{children}</>;
  }

  // 일반 관리자/오너 확인
  if (
    currentUser &&
    (currentUser.role === 'admin' || currentUser.role === 'owner')
  ) {
    return <>{children}</>;
  }

  // 인증되지 않은 경우 오너 로그인 페이지로 리다이렉트
  // 운영/STG 도메인은 /owner/login 사용. 내부망/로컬만 /studyroom/owner/login 사용.
  const hostname = window.location.hostname;
  const useStudyroomPrefix = (
    hostname === '192.168.0.38' ||
    hostname === '180.70.238.90' ||
    hostname === 'studyroom.staging.local' ||
    hostname === 'localhost' ||
    hostname.startsWith('192.168.')
  );
  const ownerLoginPath = useStudyroomPrefix ? '/studyroom/owner/login' : '/owner/login';

  return <Navigate to={ownerLoginPath} state={{ from: location }} replace />;
};

export default ProtectedRoute;
